/**
 * The order secret for the X-Order-Token header (DEVA-232, backend QA-161).
 *
 * Once the backend turns ORDER_ACCESS_TOKEN_REQUIRED on, every request that reads or changes
 * an order must either come from the order's logged-in owner or carry the order's secret in
 * X-Order-Token — otherwise it answers 403. The secret reaches the browser two ways:
 * - POST /orders returns it once, in data.order.access_token;
 * - the car-choice email link (mail_car) carries it in the #t= fragment (DEVA-224).
 *
 * It is kept next to the order id in sessionStorage (survives a reload, like the order id
 * itself) and is never put into a URL. The #t= fragment is taken out of the address bar by an
 * inline script at the top of index.html — before vue-router captures the location and before
 * PageSense/GTM read it (doing it from main.js is too late: importing the router already
 * captured the location). That script writes ORDER_TOKENS_STORAGE_KEY in the format read here.
 */
import type { InternalAxiosRequestConfig } from 'axios'

export const ORDER_TOKEN_HEADER = 'X-Order-Token'
export const ORDER_TOKENS_STORAGE_KEY = 'order_access_tokens'

// Keeping a handful is enough: a customer works with one order at a time, a repeat ride or a
// second email link adds one more.
const MAX_STORED_TOKENS = 10

interface StoredTokens {
  current: string | null
  byOrder: Record<string, string>
}

// Routes the backend gates with the order secret (booking-platform-backend routes/api.php,
// middleware order_access_token). Group 1, when present, is the id the route is keyed by:
// the order id for orders/*, fields/search and transaction/fetch.
const GATED_ROUTES: RegExp[] = [
  /^orders\/(\d+)(?:\/updateDetails)?$/,
  /^orders\/update\/[A-Za-z]+\/(\d+)$/,
  /^fields\/search\/(\d+)$/,
  /^transaction\/fetch\/(\d+)$/,
  /^transaction\/init\/[^/]+$/,
  /^transaction\/update$/,
  /^profileCompletion\/[^/]+$/,
  /^success\/editContact$/
]

const isOrderId = (value: unknown): value is string | number =>
  (typeof value === 'number' && Number.isInteger(value) && value > 0) ||
  (typeof value === 'string' && /^[1-9]\d*$/.test(value.trim()))

const isToken = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z0-9]{16,128}$/.test(value)

function read(): StoredTokens {
  try {
    const raw = sessionStorage.getItem(ORDER_TOKENS_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    if (parsed && typeof parsed === 'object') {
      return {
        current: isToken(parsed.current) ? parsed.current : null,
        byOrder: parsed.byOrder && typeof parsed.byOrder === 'object' ? parsed.byOrder : {}
      }
    }
  } catch {
    // unreadable or blocked storage — behave as if nothing is stored
  }
  return { current: null, byOrder: {} }
}

function write(tokens: StoredTokens): void {
  try {
    sessionStorage.setItem(ORDER_TOKENS_STORAGE_KEY, JSON.stringify(tokens))
  } catch {
    // storage full or blocked — the request will go without the header, as before
  }
}

/** Remembers the secret of an order; the last one remembered is the current one. */
export function rememberOrderToken(orderId: unknown, token: unknown): void {
  if (!isToken(token)) return

  const tokens = read()
  tokens.current = token

  if (isOrderId(orderId)) {
    const key = String(orderId).trim()
    delete tokens.byOrder[key]
    tokens.byOrder[key] = token
    const keys = Object.keys(tokens.byOrder)
    for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_STORED_TOKENS))) {
      delete tokens.byOrder[stale]
    }
  }

  write(tokens)
}

/**
 * Takes the secret from a POST /orders response ({ data: { order: { id, access_token } } }).
 * Called from the response interceptors, so every place that creates an order is covered.
 */
export function rememberOrderTokenFromResponse<T>(response: T): T {
  const r = response as {
    config?: InternalAxiosRequestConfig
    data?: { data?: { order?: { id?: unknown; access_token?: unknown } } }
  }
  const isOrderCreation =
    r?.config?.method?.toLowerCase() === 'post' && r.config && routeOf(r.config) === 'orders'
  const order = r?.data?.data?.order
  if (isOrderCreation && order) {
    rememberOrderToken(order.id, order.access_token)
    // The caller merges the order into orderData, which is re-sent and reported to Sentry as
    // request_payload on errors — the secret must live only in ORDER_TOKENS_STORAGE_KEY.
    delete order.access_token
  }
  return response
}

/** The secret for an order, or the current one when the order is not known by id. */
export function getOrderToken(orderId?: unknown): string | null {
  const tokens = read()
  if (isOrderId(orderId)) {
    const own = tokens.byOrder[String(orderId).trim()]
    if (own) return own
  }
  return tokens.current
}

function originOf(url: string): string | null {
  try {
    return new URL(url, window.location.origin).origin
  } catch {
    return null
  }
}

function routeOf(config: InternalAxiosRequestConfig): string | null {
  const url = config.url ?? ''
  // absolute or protocol-relative URL: not our API's relative route
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || /^[\\/]{2}/.test(url)) return null
  // a base URL other than the main backend's (set per instance or per request) is another host
  const apiUrl = import.meta.env.VITE_APP_API_URL as string | undefined
  if (config.baseURL && apiUrl && originOf(config.baseURL) !== originOf(apiUrl)) return null
  return url
    .split('?')[0]
    .replace(/^\/+/, '')
    .replace(/^api\//, '')
}

/** Whether a request goes to a route the backend gates with the order secret. */
export function isOrderGatedRequest(config: InternalAxiosRequestConfig): boolean {
  const route = routeOf(config)
  return route !== null && GATED_ROUTES.some((pattern) => pattern.test(route))
}

function bodyOrderId(data: unknown): unknown {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)?.order_id
    } catch {
      return undefined
    }
  }
  return (data as { order_id?: unknown } | null | undefined)?.order_id
}

/**
 * Adds X-Order-Token to a request for a gated order route. The token of the order named in
 * the route (or in order_id of the body) wins; otherwise the current one is sent. Requests to
 * any other route, or to another backend, never get it.
 */
export function injectOrderToken(
  config: InternalAxiosRequestConfig,
  options: { skip?: boolean } = {}
): InternalAxiosRequestConfig {
  if (options.skip || !config.headers || config.headers[ORDER_TOKEN_HEADER]) return config

  const route = routeOf(config)
  if (route === null) return config

  const pattern = GATED_ROUTES.find((p) => p.test(route))
  if (!pattern) return config

  const orderId = route.match(pattern)?.[1] ?? bodyOrderId(config.data)
  const token = getOrderToken(orderId)
  if (token) {
    config.headers[ORDER_TOKEN_HEADER] = token
  }
  return config
}

export const ORDER_FORBIDDEN_MESSAGE =
  'This booking cannot be opened from this browser. Please use the link from your email or sign in to your account.'

/** A 403 from a gated order route — the order secret is missing or wrong. */
export function isOrderForbiddenError(error: unknown): boolean {
  const e = error as { response?: { status?: number }; config?: InternalAxiosRequestConfig }
  return e?.response?.status === 403 && Boolean(e.config) && isOrderGatedRequest(e.config!)
}
